export const LONG_PRESS_MS = 700;
export const LONG_PRESS_MOVE_THRESHOLD = 10;

export function filterSongs<T extends { title: string; author: string | null }>(
  songs: T[],
  query: string,
): T[] {
  const normalize = (value: string) =>
    value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase('it')
      .trim();
  const terms = normalize(query).split(/\s+/).filter(Boolean);
  if (terms.length === 0) return songs;
  return songs.filter((song) => {
    const searchable = normalize(`${song.title} ${song.author ?? ''}`);
    return terms.every((term) => searchable.includes(term));
  });
}

export type WorkspaceView = 'home' | 'setlists' | 'setlist' | 'library' | 'inbox' | 'map';
export type WorkspacePanel =
  | 'menu'
  | 'create-setlist'
  | 'rename-setlist'
  | 'rename-group'
  | 'song-form'
  | 'item-form'
  | 'invite'
  | 'collaborators'
  | 'profile'
  | 'guide'
  | 'actions'
  | 'library-actions'
  | 'song-destination'
  | 'insert-box'
  | null;

export type WorkspaceUiState = { view: WorkspaceView; panel: WorkspacePanel };
export type WorkspaceUiAction =
  | { type: 'navigate'; view: WorkspaceView }
  | { type: 'open-setlist' }
  | { type: 'open-panel'; panel: Exclude<WorkspacePanel, null> }
  | { type: 'close-panel' };

export function transitionWorkspaceUi(
  state: WorkspaceUiState,
  action: WorkspaceUiAction,
): WorkspaceUiState {
  if (action.type === 'navigate') return { view: action.view, panel: null };
  if (action.type === 'open-setlist') return { view: 'setlist', panel: null };
  if (action.type === 'open-panel') return { ...state, panel: action.panel };
  return { ...state, panel: null };
}

export function isBottomNavActive(current: WorkspaceView, target: WorkspaceView) {
  if (target === 'setlists') return current === 'setlists' || current === 'setlist';
  return current === target;
}

export function canModifySetlist(setlistId: string) {
  return setlistId.length > 0;
}

export function moveDestination(currentIndex: number, insertionIndex: number) {
  const adjusted = insertionIndex > currentIndex ? insertionIndex - 1 : insertionIndex;
  return Math.max(0, adjusted);
}

export function unreadTransferItemCount(
  transfers: Array<{ id: string; status: string; items: unknown[] }>,
  readIds: ReadonlySet<string>,
) {
  return transfers
    .filter((transfer) => transfer.status === 'pending' && !readIds.has(transfer.id))
    .reduce((sum, transfer) => sum + transfer.items.length, 0);
}

export function youtubeVideoId(sourceUrl: string | null | undefined) {
  if (!sourceUrl) return null;
  try {
    const url = new URL(sourceUrl);
    const host = url.hostname.toLowerCase().replace(/^www\./, '');
    if (host === 'youtu.be') return url.pathname.split('/').filter(Boolean)[0] ?? null;
    if (
      host === 'youtube.com' ||
      host === 'm.youtube.com' ||
      host === 'music.youtube.com'
    ) {
      if (url.pathname === '/watch') return url.searchParams.get('v');
      const parts = url.pathname.split('/').filter(Boolean);
      if (['embed', 'shorts', 'live'].includes(parts[0])) return parts[1] ?? null;
    }
  } catch {
    return null;
  }
  return null;
}

export type InboxTransfer = {
  id: string;
  created_at?: string | null;
  sender_id?: string | null;
  origin?: string | null;
};

export type InboxSort = 'newest' | 'oldest';

export function filterAndSortInbox<T extends InboxTransfer>(
  transfers: T[],
  senderId: string,
  sort: InboxSort,
) {
  return transfers
    .filter((transfer) => {
      if (!senderId) return true;
      if (senderId === 'self') return transfer.origin === 'self_import';
      return transfer.sender_id === senderId;
    })
    .sort((left, right) => {
      const leftTime = left.created_at ? Date.parse(left.created_at) : 0;
      const rightTime = right.created_at ? Date.parse(right.created_at) : 0;
      return sort === 'newest' ? rightTime - leftTime : leftTime - rightTime;
    });
}

export function inboxDayKey(createdAt?: string | null) {
  if (!createdAt || Number.isNaN(Date.parse(createdAt))) return 'legacy';
  const value = new Date(createdAt);
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
}

export function groupInboxByDay<T extends InboxTransfer>(transfers: T[]) {
  return transfers.reduce<Array<{ day: string; transfers: T[] }>>((groups, transfer) => {
    const day = inboxDayKey(transfer.created_at);
    const previous = groups.at(-1);
    if (previous?.day === day) previous.transfers.push(transfer);
    else groups.push({ day, transfers: [transfer] });
    return groups;
  }, []);
}

export type SearchableCollaborator = {
  profile_id: string;
  display_name: string;
  instrument_role: string;
  phone: string;
};

export function filterCollaborators<T extends SearchableCollaborator>(
  collaborators: T[],
  query: string,
) {
  const normalized = query.trim().toLocaleLowerCase('it-IT').replace(/\s/g, '');
  if (!normalized) return collaborators;
  return collaborators.filter(
    (person) =>
      person.display_name
        .toLocaleLowerCase('it-IT')
        .replace(/\s/g, '')
        .includes(normalized) ||
      person.instrument_role
        .toLocaleLowerCase('it-IT')
        .replace(/\s/g, '')
        .includes(normalized) ||
      person.phone.replace(/\s/g, '').includes(normalized),
  );
}

export function splitRecentCollaborators<T extends SearchableCollaborator>(
  collaborators: T[],
  recentIds: string[],
) {
  const recent = recentIds
    .map((id) => collaborators.find((person) => person.profile_id === id))
    .filter((person): person is T => !!person);
  const others = collaborators.filter(
    (person) => !recent.some((entry) => entry.profile_id === person.profile_id),
  );
  return { others, recent };
}

export function recordRecentCollaborator(
  current: string[],
  profileId: string,
  limit = 5,
) {
  return [profileId, ...current.filter((id) => id !== profileId)].slice(0, limit);
}

export function selectionActions({
  selectedCount,
  setlistId,
}: {
  selectedCount: number;
  setlistId: string;
}) {
  const canModify = selectedCount > 0 && canModifySetlist(setlistId);
  return {
    canSend: selectedCount > 0,
    canAddToSetlist: canModify,
    canCreateBlock: canModify && selectedCount > 1,
  };
}

export type SongContextAction = {
  id: 'edit' | 'move' | 'remove' | 'branch' | 'lock' | 'alternative';
  label: string;
  enabled: boolean;
};

export function songContextActions(_inBlock: boolean): SongContextAction[] {
  return [
    { id: 'edit', label: 'Modifica', enabled: true },
    { id: 'move', label: 'Sposta', enabled: true },
    { id: 'remove', label: 'Elimina', enabled: true },
    { id: 'branch', label: 'Bivio', enabled: true },
    { id: 'lock', label: 'Blocca', enabled: true },
    { id: 'alternative', label: 'Alternativa', enabled: true },
  ];
}

export type BatchResult = {
  completedIds: string[];
  pendingIds: string[];
  error: string | null;
};

export async function runSequentially(
  ids: string[],
  operation: (id: string) => Promise<{ ok: true } | { ok: false; error: string }>,
): Promise<BatchResult> {
  const completedIds: string[] = [];
  for (let index = 0; index < ids.length; index += 1) {
    const result = await operation(ids[index]);
    if (!result.ok) {
      return { completedIds, pendingIds: ids.slice(index), error: result.error };
    }
    completedIds.push(ids[index]);
  }
  return { completedIds, pendingIds: [], error: null };
}

type Schedule = (callback: () => void, milliseconds: number) => unknown;
type Cancel = (timer: unknown) => void;

export class LongPressController {
  private timer: unknown = null;
  private origin: { x: number; y: number } | null = null;
  private triggered = false;
  private moved = false;

  constructor(
    private readonly schedule: Schedule = (callback, milliseconds) =>
      window.setTimeout(callback, milliseconds),
    private readonly cancelTimer: Cancel = (timer) =>
      window.clearTimeout(timer as number),
  ) {}

  start(x: number, y: number, onTrigger: () => void) {
    this.cancel();
    this.origin = { x, y };
    this.triggered = false;
    this.moved = false;
    this.timer = this.schedule(() => {
      this.timer = null;
      this.triggered = true;
      onTrigger();
    }, LONG_PRESS_MS);
  }

  move(x: number, y: number) {
    if (!this.origin || this.timer === null) return;
    if (Math.hypot(x - this.origin.x, y - this.origin.y) < LONG_PRESS_MOVE_THRESHOLD)
      return;
    this.moved = true;
    this.clearTimer();
  }

  end() {
    this.clearTimer();
    this.origin = null;
  }

  cancel() {
    this.clearTimer();
    this.origin = null;
  }

  consumeTapSuppression() {
    const suppress = this.triggered || this.moved;
    this.triggered = false;
    this.moved = false;
    return suppress;
  }

  clearTapSuppression() {
    this.triggered = false;
    this.moved = false;
  }

  private clearTimer() {
    if (this.timer !== null) this.cancelTimer(this.timer);
    this.timer = null;
  }
}
